import {
  AorCancellationStatus,
  CarrierRequestStatus,
  EnrollmentStatus,
  isDefined,
} from '@zorro/clients';
import { checkCobraEligibility } from '@zorro/common-utils';

import { paymentsTabEnabledEnrollmentStatuses } from '../EnrollmentStatus/enrollmentStatus.consts';
import { type RecordForActions } from './EmployeeActionsUtils';

function isEnrollmentStatus(
  status: EnrollmentStatus | undefined
): status is EnrollmentStatus {
  return status !== undefined;
}

export function calculateElectionWindowLaunchStatus(
  recordsForActions: RecordForActions[]
) {
  const enrollmentStatuses = recordsForActions.map(
    (record) => record.enrollmentStatus
  );

  if (enrollmentStatuses.length === 0) {
    return false;
  }

  // if any status is undefined, return false
  if (!enrollmentStatuses.every((status) => isEnrollmentStatus(status))) {
    return false;
  }

  const electionWindowLaunchStatuses = new Set([
    EnrollmentStatus.PENDING_ELECTION_WINDOW,
    EnrollmentStatus.ELECTION_ACTIVE_STARTED,
    EnrollmentStatus.ELECTION_ACTIVE_HAS_NOT_STARTED,
  ]);

  return enrollmentStatuses.every((status) =>
    electionWindowLaunchStatuses.has(status)
  );
}

export function getOnboardingPeriodsStatus(
  recordsForActions: RecordForActions[]
) {
  const enrollmentStatuses = recordsForActions.map(
    (record) => record.enrollmentStatus
  );

  const defaultReturn = {
    areAllOnboardingPeriodsExist: false,
    areAllOnboardingPeriodsActive: false,
    areAllOnboardingPeriodsSubmitted: false,
    areAllOnboardingPeriodsEnrolled: false,
    areAllOnboardingPeriodsWaived: false,
    areAllOnboardingPeriodsAtCarrierApplicationSentOrAfter: false,
    areAllBenefitsSubmitted: false,
    areAnyBenefitsSubmitted: false,
  };

  if (enrollmentStatuses.length === 0) {
    return defaultReturn;
  }

  // if any status is undefined, set all to false
  if (!enrollmentStatuses.every((status) => isEnrollmentStatus(status))) {
    return defaultReturn;
  }

  const doNotExistStatuses = new Set([EnrollmentStatus.NO_ENROLLMENTS]);

  const areAllOnboardingPeriodsExist = enrollmentStatuses.every(
    (status) => !doNotExistStatuses.has(status)
  );

  const activeStatuses = new Set([
    EnrollmentStatus.ELECTION_ACTIVE_STARTED,
    EnrollmentStatus.ELECTION_ACTIVE_HAS_NOT_STARTED,
  ]);

  const areAllOnboardingPeriodsActive = enrollmentStatuses.every((status) =>
    activeStatuses.has(status)
  );

  const submittedStatuses = new Set([
    EnrollmentStatus.ELECTION_SUBMITTED,
    EnrollmentStatus.ENROLLMENT_CONFIRMED,
    EnrollmentStatus.ENROLLMENT_IN_PROCESS,
    EnrollmentStatus.ACTIVE_COVERAGE,
    EnrollmentStatus.COVERAGE_ENDED,
  ]);

  const areAllOnboardingPeriodsSubmitted = enrollmentStatuses.every((status) =>
    submittedStatuses.has(status)
  );

  const waivedStatuses = new Set([
    EnrollmentStatus.WAIVED_ELECTION,
    EnrollmentStatus.WAIVED_COVERAGE,
  ]);

  const enrolledStatuses = new Set([
    EnrollmentStatus.ENROLLMENT_CONFIRMED,
    EnrollmentStatus.ACTIVE_COVERAGE,
    EnrollmentStatus.COVERAGE_ENDED,
  ]);

  const areAllOnboardingPeriodsWaived = enrollmentStatuses.every((status) =>
    waivedStatuses.has(status)
  );

  const areAllOnboardingPeriodsEnrolled = enrollmentStatuses.every((status) =>
    enrolledStatuses.has(status)
  );

  const areAllOnboardingPeriodsAtCarrierApplicationSentOrAfter =
    enrollmentStatuses.every((status) =>
      paymentsTabEnabledEnrollmentStatuses.has(status)
    );

  const preSubmissionStatuses = new Set([
    EnrollmentStatus.PENDING_ELECTION_WINDOW,
    EnrollmentStatus.DEADLINE_PASSED,
    EnrollmentStatus.ELECTION_ACTIVE_STARTED,
    EnrollmentStatus.ELECTION_ACTIVE_HAS_NOT_STARTED,
  ]);

  const areAllBenefitsSubmitted = enrollmentStatuses.every(
    (status) => !preSubmissionStatuses.has(status)
  );
  const areAnyBenefitsSubmitted = enrollmentStatuses.some(
    (status) => !preSubmissionStatuses.has(status)
  );

  return {
    areAllOnboardingPeriodsExist,
    areAllOnboardingPeriodsActive,
    areAllOnboardingPeriodsSubmitted,
    areAllOnboardingPeriodsEnrolled,
    areAllOnboardingPeriodsWaived,
    areAllOnboardingPeriodsAtCarrierApplicationSentOrAfter,
    areAllBenefitsSubmitted,
    areAnyBenefitsSubmitted,
  };
}

export function computeAreAllAorStatusesEligible(
  recordsForActions: RecordForActions[]
) {
  const aorStatuses = recordsForActions.map(
    (record) => record.aorCancellationStatus
  );

  return aorStatuses.every(
    (status) =>
      status === AorCancellationStatus.CONFIRMED_BY_EMPLOYEE ||
      status === AorCancellationStatus.AWAITING_EMPLOYEE
  );
}

export function computeAreAllCarrierRequestStatusesEligible(
  recordsForActions: RecordForActions[]
) {
  const carrierRequestStatuses = recordsForActions.map(
    (record) => record.carrierRequestStatus
  );

  return carrierRequestStatuses.every(
    (status) =>
      status === CarrierRequestStatus.CONFIRMED_BY_EMPLOYEE ||
      status === CarrierRequestStatus.AWAITING_EMPLOYEE
  );
}

export function getEmployeeNameOrEmployeesCount(
  recordsForActions: RecordForActions[]
) {
  const isSingleEmployee = recordsForActions.length === 1;

  if (isSingleEmployee) {
    return recordsForActions[0].fullName;
  }

  return `${recordsForActions.length} employees`;
}

export function getCobraEligibilityStatus(
  recordsForActions: RecordForActions[]
): boolean {
  if (recordsForActions.length === 0) {
    return false;
  }

  return recordsForActions.every((record) => {
    if (
      !record.enrollmentStartDate ||
      !record.enrollmentEndDate ||
      !isDefined(record.isOnboardingWaived)
    ) {
      return false;
    }

    return checkCobraEligibility(
      record.enrollmentStartDate,
      record.enrollmentEndDate,
      record.benefitStatus ?? null,
      record.isOnboardingWaived,
      record.terminationDate ?? null,
      record.eligibleUntil ?? null
    );
  });
}

export const actionsMenuTooltipLabels = {
  areAllOnboardingPeriodsExist: 'Has an enrollment record',
  isStateEqual: 'Same state',
  isStartDateEqual: 'Same start date',
  isCarrierEqual: 'Same carrier',
  isEnrollmentStatus: "Status: 'Election submitted'",
  isCombinedPlanEqual: 'Not a combined household',
  isSingleEmployer: 'Same employer',
  isSingleEmployerOrSharedTeam: 'Same employer or shared enrollment team',
  areAllOnboardingPeriodsActive:
    "Status: 'Election active: started' or 'Election active: has not started'",
  areAllOnboardingPeriodsSubmitted:
    "Status: 'Election submitted', 'Enrollment confirmed', 'Carrier application sent', 'Active coverage' or 'Coverage ended'",
  areAllOnboardingPeriodsEnrolled:
    "Status: 'Enrollment confirmed', 'Active coverage' or 'Coverage ended'",
  areAllOnboardingPeriodsWaived:
    "Status: 'Waived election' or 'Waived coverage'",
  areAnyBenefitsSubmitted:
    "Status: 'Pending election window', 'Election active: started', 'Election active: has not started' or 'Deadline passed'",
  areAllBenefitsSubmitted:
    "Status: 'Election submitted', 'Enrollment confirmed', 'Carrier application sent', 'Active coverage', 'Coverage ended', 'Waived election' or 'Waived coverage'",
  isElectionWindowLaunch:
    "Status: 'Pending election window', 'Election active: started' or 'Election active: has not started'",
  isNotSelfEnroll: 'Not self-enroll',
  areAllAorStatusesEligible: 'AOR cancellation status applicable',
  areAllCarrierRequestStatusesEligible: 'Carrier request status applicable',
  isAtCarrierApplicationSentOrAfter:
    "Status: 'Carrier application sent', 'Enrollment confirmed', 'Active coverage' or 'Coverage ended'",
  areAllCobraEligible:
    'Has termination date, eligible until date, and enrollment confirmed/active/ended',
  hasBenefit: 'Has benefit',
} as const;
